/**
 * ReparateursPage — externe reparateurs beheren (admin-only).
 *
 * Een reparateur = SF Account met Is_Reparateur__c=true. Aanmaken hier zet 'm
 * direct in de "Reparatie aanvragen"-dropdown; portaaltoegang
 * (reparateur.kleda.nl) regelt zichzelf bij eerste login op het e-mailadres.
 *
 * Backend: GET/POST/PATCH /api/reparateurs → Apex KledaReparatieOfficeRest.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  createReparateur,
  listReparateursAdmin,
  updateReparateur,
  type ReparateurDto,
} from '../lib/reparatie';

const PORTAL_URL = 'reparateur.kleda.nl';

function margeLabel(pct: number | null): string {
  return `${pct ?? 50}% marge`;
}

interface NewReparateurFormProps {
  onCreated: () => void;
  onCancel: () => void;
  onError: (msg: string) => void;
}

function NewReparateurForm({ onCreated, onCancel, onError }: NewReparateurFormProps) {
  const [form, setForm] = useState({ naam: '', email: '', marge: '50' });
  const [created, setCreated] = useState<{ naam: string; email: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!form.naam.trim() || !form.email.trim()) {
      onError('Naam en e-mailadres zijn verplicht');
      return;
    }
    setBusy(true);
    try {
      const res = await createReparateur({
        naam: form.naam.trim(),
        email: form.email.trim(),
        margePercentage: form.marge.trim() === '' ? null : Number(form.marge),
      });
      if (!res.success) throw new Error(res.message || 'Aanmaken mislukt');
      setCreated({ naam: form.naam.trim(), email: form.email.trim() });
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Aanmaken mislukt');
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <div className="bg-white rounded-xl border border-indigo-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-1">Reparateur aangemaakt</h3>
        <p className="text-sm text-gray-600">
          <strong>{created.naam}</strong> staat nu in de “Reparatie aanvragen”-dropdown.
        </p>
        <div className="mt-3 border border-emerald-200 bg-emerald-50/60 rounded-lg p-4 text-sm text-emerald-900 space-y-1">
          <p>Portaaltoegang regelt zichzelf — stuur ze naar:</p>
          <p>
            <a
              href={`https://${PORTAL_URL}`}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-indigo-700 hover:underline"
            >
              {PORTAL_URL} ↗
            </a>
          </p>
          <p>
            Laten inloggen met <strong>{created.email}</strong> — ze krijgen dan een magic-link
            en zien hun reparatie-opdrachten.
          </p>
        </div>
        <button
          onClick={onCreated}
          className="mt-3 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium"
        >
          Sluiten
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-indigo-200 p-5">
      <h3 className="font-semibold text-gray-900 mb-3">Nieuwe reparateur</h3>
      <div className="grid grid-cols-2 gap-3 max-w-2xl">
        <input
          value={form.naam}
          onChange={(e) => setForm({ ...form, naam: e.target.value })}
          placeholder="Naam (bv. Perzisch Tapijtenhuis Apeldoorn)"
          className="col-span-2 px-2 py-1.5 border border-slate-300 rounded-md text-sm"
        />
        <input
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="E-mailadres (portaal-login)"
          inputMode="email"
          className="px-2 py-1.5 border border-slate-300 rounded-md text-sm"
        />
        <input
          value={form.marge}
          onChange={(e) => setForm({ ...form, marge: e.target.value })}
          placeholder="Marge % (50)"
          inputMode="decimal"
          className="px-2 py-1.5 border border-slate-300 rounded-md text-sm"
        />
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Het e-mailadres wordt de portaal-login op {PORTAL_URL}. Marge bepaalt de standaard-opslag op
        reparatie-offertes (leeg = 50%).
      </p>
      <div className="flex gap-2 mt-3">
        <button
          onClick={create}
          disabled={busy}
          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
        >
          {busy ? 'Aanmaken…' : 'Reparateur aanmaken'}
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900">
          Annuleren
        </button>
      </div>
    </div>
  );
}

interface ReparateurCardProps {
  reparateur: ReparateurDto;
  onChanged: () => void;
  onError: (msg: string) => void;
}

function ReparateurCard({ reparateur: r, onChanged, onError }: ReparateurCardProps) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    naam: r.naam,
    email: r.email ?? '',
    marge: String(r.margePercentage ?? 50),
  });
  const [busy, setBusy] = useState<string | null>(null);

  const save = async () => {
    if (!form.naam.trim() || !form.email.trim()) {
      onError('Naam en e-mailadres mogen niet leeg zijn');
      return;
    }
    setBusy('save');
    try {
      const res = await updateReparateur({
        reparateurId: r.id,
        naam: form.naam.trim(),
        email: form.email.trim(),
        margePercentage: form.marge.trim() === '' ? null : Number(form.marge),
      });
      if (!res.success) throw new Error(res.message || 'Opslaan mislukt');
      setEditing(false);
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Opslaan mislukt');
    } finally {
      setBusy(null);
    }
  };

  const deactivate = async () => {
    if (!window.confirm(`${r.naam} deactiveren? Ze verdwijnen uit de dropdown en verliezen portaaltoegang.`)) {
      return;
    }
    setBusy('deactivate');
    try {
      const res = await updateReparateur({ reparateurId: r.id, actief: false });
      if (!res.success) throw new Error(res.message || 'Deactiveren mislukt');
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Deactiveren mislukt');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      {editing ? (
        <div className="max-w-2xl space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input
              value={form.naam}
              onChange={(e) => setForm({ ...form, naam: e.target.value })}
              placeholder="Naam"
              className="col-span-2 px-2 py-1.5 border border-slate-300 rounded-md text-sm"
            />
            <input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="E-mailadres"
              inputMode="email"
              className="px-2 py-1.5 border border-slate-300 rounded-md text-sm"
            />
            <input
              value={form.marge}
              onChange={(e) => setForm({ ...form, marge: e.target.value })}
              placeholder="Marge %"
              inputMode="decimal"
              className="px-2 py-1.5 border border-slate-300 rounded-md text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={save}
              disabled={busy === 'save'}
              className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
            >
              Opslaan
            </button>
            <button onClick={() => setEditing(false)} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900">
              Annuleren
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900">{r.naam}</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {r.email || <span className="text-amber-600">géén e-mailadres</span>}
              {' · '}
              {margeLabel(r.margePercentage)}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => {
                setForm({ naam: r.naam, email: r.email ?? '', marge: String(r.margePercentage ?? 50) });
                setEditing(true);
              }}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Bewerken
            </button>
            <button
              onClick={deactivate}
              disabled={busy === 'deactivate'}
              className="text-sm text-gray-500 hover:text-red-600 disabled:opacity-50"
            >
              Deactiveren
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function ReparateursPage() {
  const [reparateurs, setReparateurs] = useState<ReparateurDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await listReparateursAdmin();
      setReparateurs(list);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fout bij laden');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="flex items-start justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Reparateurs</h1>
              <p className="text-gray-500 mt-1">
                Externe reparateurs voor de reparatieflow — verschijnen in de “Reparatie aanvragen”-dropdown
                en loggen in op {PORTAL_URL}
              </p>
            </div>
            {!showNew && (
              <button
                onClick={() => setShowNew(true)}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium shrink-0"
              >
                + Nieuwe reparateur
              </button>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start justify-between gap-4">
              <p className="text-red-800 text-sm">{error}</p>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-700 text-sm">
                ✕
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              {showNew && (
                <NewReparateurForm
                  onCreated={() => {
                    setShowNew(false);
                    load();
                  }}
                  onCancel={() => setShowNew(false)}
                  onError={setError}
                />
              )}
              {reparateurs.map((r) => (
                <ReparateurCard key={r.id} reparateur={r} onChanged={load} onError={setError} />
              ))}
              {reparateurs.length === 0 && !showNew && (
                <p className="text-sm text-gray-500">Nog geen reparateurs. Maak er een aan met “+ Nieuwe reparateur”.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
